import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Field } from '@/components/forge/Field'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeSelect, StaticField } from '@/components/forge/ForgeSelect'
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { Eyebrow, Mono, Num } from '@/components/forge/primitives'
import {
  DESCRIPTION_MAX_LENGTH,
  NAME_MAX_LENGTH,
  VALUE_TYPES,
  waveformTypeById,
} from '@/features/sensors/api'
import { PARENT_ICON, describeParent, parentOf } from '@/features/hierarchy/parents'
import { ConfigField } from './ConfigField'
import { JsonPathBuilder } from './JsonPathBuilder'
import { PlaceField } from '@/components/forge/PlaceField'

/**
 * One sensor's whole form, shared by onboarding and editing.
 *
 * ── The shape follows the resource: one device, one or more signals ───────
 * `Sensor` carries identity, placement and the extraction settings its tags
 * share; each `SensorTag` carries one signal. So the form is a device block and a
 * repeatable signal block, and `tags` is `min_length=1` server-side — a device
 * with no signal is not a thing that can exist.
 *
 * It used to be one flat panel set describing a single signal, from back when
 * those columns lived on the sensor itself. That form could not create a
 * multi-signal device at all, and opening a stored one showed the first signal
 * and silently dropped the rest on save.
 *
 * ── Why one scrolling form and not a wizard ───────────────────────────────
 * The connection wizard next door has four steps because it earns them: it has a
 * catalogue to choose from and a network probe that gates the write. A sensor has
 * neither. It is also the repetitive one — a connection is created once, sensors
 * are created in dozens, and every step boundary is paid again on each.
 *
 * ── Shared settings and per-signal overrides ──────────────────────────────
 * The extraction fields appear TWICE, and that is the contract rather than a
 * duplication. `_validate_tag_extraction_config` validates
 * `{**device_config, **tag_config}` per tag, so a key answered once on the device
 * covers every signal and a key answered on a signal wins for that signal alone.
 * A Sparkplug device has one `topic` and three different `value_path`s; nothing
 * in this file knows which of its fields is which, and it must not — the schema
 * is the connector's and a new one ships without a frontend release.
 *
 * So the device panel asks for all of them and the signal panel offers all of
 * them as overrides, blank meaning "use the device's". `buildSparseConfig` is
 * what makes blank mean that on the wire.
 *
 * ── The connection is context, not a field ────────────────────────────────
 * Onboarding is always entered from one connection's own screen, and
 * `PUT /sensors/{id}` does not accept `sensor_connection_id`, so it cannot be
 * changed afterwards either. Stated as a `StaticField` in both modes rather than
 * drawn as a disabled select — a disabled control looks like a control, takes a
 * hover, and teaches someone to keep trying it.
 *
 * @param {object} props
 * @param {{id: string, name: string}} props.connection
 * @param {?{name: string, protocolFamily: string, extractionFields: Array<object>}} props.connector
 * @param {object} props.hierarchy      `useHierarchyNodes()` result.
 * @param {object} props.assetClasses   `useAssetClasses()` result.
 * @param {Array<object>} props.waveformTypes  The seeded waveform catalogue.
 * @param {Array<object>} props.valueUnits     The seeded engineering units.
 * @param {object} props.values
 * @param {object} props.errors
 * @param {(patch: object) => void} props.onChange
 * @param {(key: string, value: *) => void} props.onExtractionChange
 * @param {(index: number, patch: object) => void} props.onTagChange
 * @param {(index: number, key: string, value: *) => void} props.onTagExtractionChange
 * @param {(index: number, key: string, value: *) => void} props.onTagMetadataChange
 * @param {() => void} props.onAddTag
 * @param {(index: number) => void} props.onRemoveTag
 * @param {boolean} [props.parentLocked]
 *   Edit mode. `SensorUpdate` accepts no parent field, so the cascade is replaced
 *   by the resolved placement stated as fact. Offering the control would be worse
 *   than hiding it: the schema sets no `model_config`, so Pydantic ignores the
 *   extra keys and a re-attach would report success and change nothing.
 */
export function SensorForm({
  connection,
  connector,
  hierarchy,
  assetClasses,
  waveformTypes,
  valueUnits,
  values,
  errors,
  onChange,
  onExtractionChange,
  onTagChange,
  onTagExtractionChange,
  onTagMetadataChange,
  onAddTag,
  onRemoveTag,
  parentLocked = false,
}) {
  const { t } = useTranslation(['app', 'common'])

  /**
   * Which jsonpath field has the builder open, and whose.
   *
   * `{field, scope}` where scope is `'device'` or a tag index — the same schema
   * field is rendered once per signal plus once on the device, so the field alone
   * no longer identifies a control. Purely local chrome.
   */
  const [building, setBuilding] = useState(null)

  // Only sensor-typed classes. `ENTITY_TYPE_FOR_TIER` cannot supply this — it maps
  // hierarchy tiers, and a sensor is not a hierarchy node — so `'sensor'` is named
  // directly. The list is fetched unfiltered and narrowed here, which is the same
  // thing SitesHierarchyPage does with it.
  const sensorClasses = (assetClasses.data?.items ?? []).filter(
    (entry) => entry.entityType === 'sensor',
  )

  const extractionFields = connector?.extractionFields ?? []

  const buildingValue =
    building === null
      ? ''
      : building.scope === 'device'
        ? (values.extraction[building.field.key] ?? '')
        : (values.tags[building.scope]?.extraction[building.field.key] ?? '')

  return (
    <div className="flex max-w-[76rem] flex-col gap-5">
      {/* ── The device ──────────────────────────────────────────────────
          Two columns above xl, stacked below. The split is not decorative: it is
          what removes the scroll. Identity and placement answer "which asset is
          this", the shared extraction answers "where its values come from" — two
          independent questions, so side by side costs no reading order and halves
          the height. `items-start` keeps the shorter column from stretching. */}
      <div className="grid items-start gap-x-6 gap-y-5 xl:grid-cols-2">
        <div className="flex flex-col gap-5">
          {/* ── Identity ──────────────────────────────────────────────── */}
          <Panel className="flex flex-col gap-4">
            <Eyebrow>{t('dataFusion.sensors.sections.identity')}</Eyebrow>
            <Field
              id="sensor-name"
              label={t('dataFusion.sensors.fields.name')}
              hint={t('dataFusion.sensors.fields.nameHint')}
              required
              autoFocus
              maxLength={NAME_MAX_LENGTH}
              value={values.name}
              error={errors.name}
              onChange={(event) => onChange({ name: event.target.value })}
            />

            {/* Required, not optional — `Field(min_length=1)` on both create and
                update. Marked as such rather than left for the server to reject,
                because a Pydantic length constraint returns FastAPI's 422 shape and
                `apiError.js` flattens that to the key `body`, which names no
                control. So the form would show "something was wrong" beside nothing. */}
            <Field
              id="sensor-description"
              label={t('dataFusion.sensors.fields.description')}
              hint={t('dataFusion.sensors.fields.descriptionHint')}
              required
              maxLength={DESCRIPTION_MAX_LENGTH}
              value={values.description}
              error={errors.description}
              onChange={(event) => onChange({ description: event.target.value })}
            />

            <StaticField
              label={t('dataFusion.sensors.fields.connection')}
              hint={t('dataFusion.sensors.fields.connectionLocked')}
              icon="plug"
            >
              <span className="min-w-0 truncate">
                {connection.name}
                {connector ? (
                  <span className="text-ink-dim" translate="no">
                    {' · '}
                    {connector.name}
                  </span>
                ) : null}
              </span>
            </StaticField>
          </Panel>

          {/* ── Placement ─────────────────────────────────────────────── */}
          <Panel className="flex flex-col gap-4">
            <Eyebrow>{t('dataFusion.sensors.sections.placement')}</Eyebrow>

            {parentLocked ? (
              <LockedParent nodes={hierarchy.data?.items ?? []} values={values} />
            ) : hierarchy.isError ? (
              <div className="flex flex-col gap-1.5">
                <span className="text-control text-ink">
                  {t('dataFusion.sensors.fields.parent')}
                </span>
                <p className="m-0 flex items-start gap-1.5 text-body-sm text-on-red">
                  <Icon name="bolt" size="xs" className="mt-0.5 shrink-0" />
                  <span>{t('dataFusion.sensors.fields.hierarchyFailed')}</span>
                </p>
              </div>
            ) : (
              /* The shared place picker — `HierarchyPicker`, drilled and
                 searched by the server — where three cascading selects over
                 the whole estate used to be. `ParentPicker` is kept beside
                 this file, unimported; see its header. `id="sensor-site"` stays
                 on the wrapper because the error summary links to it. */
              <div id="sensor-site" tabIndex={-1}>
                <PlaceField
                  label={t('dataFusion.sensors.fields.parent')}
                  required
                  value={placeValue(hierarchy.data?.items ?? [], values.parent)}
                  onChange={(place) =>
                    onChange({ parent: place ? { tier: place.tier, id: place.id, name: place.name } : null })
                  }
                  placeholder={t('dataFusion.sensors.fields.chooseParent')}
                  hint={t('dataFusion.sensors.fields.parentHint')}
                  error={errors.parent}
                />
              </div>
            )}

            {/* ── Always rendered, even with nothing to choose ──────────────
                This used to be hidden when the account had defined no sensor
                classes, on the reasoning that a select offering only "No class" is
                a control that can only be left alone. That was wrong in the way
                that costs support time: `asset_class_id` is a real, nullable,
                round-tripping field, and a control that vanishes is
                indistinguishable from a product that has no such concept.

                So it is always here, always shows what exists, and its hint says so
                when nothing does. */}
            <ForgeSelect
              id="sensor-asset-class"
              label={t('dataFusion.sensors.fields.assetClass')}
              hint={t(
                sensorClasses.length
                  ? 'dataFusion.sensors.fields.assetClassHint'
                  : 'dataFusion.sensors.fields.assetClassNone',
              )}
              value={values.assetClassId || NO_CLASS}
              onValueChange={(next) => onChange({ assetClassId: next === NO_CLASS ? '' : next })}
              options={[
                { value: NO_CLASS, label: t('dataFusion.sensors.fields.noAssetClass') },
                ...sensorClasses.map((entry) => ({ value: entry.id, label: entry.name })),
              ]}
            />
          </Panel>
        </div>

        {/* ── Shared reading ──────────────────────────────────────────────
            The device's own `extraction_config`. Every signal below inherits
            these, and every signal can override any of them — see the note at the
            top of this file for why both halves exist. */}
        <Panel className="flex flex-col gap-4">
          <Eyebrow>{t('dataFusion.sensors.sections.shared')}</Eyebrow>
          <p className="m-0 text-body-sm text-ink-dim">
            {t('dataFusion.sensors.sharedIntro')}
          </p>

          {extractionFields.length ? (
            extractionFields.map((field) => (
              <ConfigField
                key={field.key}
                field={field}
                value={values.extraction[field.key]}
                error={errors.extraction?.[field.key]}
                onChange={(value) => onExtractionChange(field.key, value)}
                onOpenBuilder={(entry) => setBuilding({ field: entry, scope: 'device' })}
              />
            ))
          ) : (
            /* `opcua_standard.extraction_schema` is `{"fields": []}`, so this branch
               is reachable today and will stay reachable until that schema is
               seeded. The validator accepts `{}` for it, which means the sensor
               saves and has no way to say which node it reads. Saying so is the only
               honest thing the form can do — inventing a NodeId field here would be
               a field the server neither validates nor uses. */
            <div className="rounded-sm border border-hairline stripe-amber bg-card p-3.5">
              <p className="m-0 text-control text-on-amber">
                {t('dataFusion.sensors.noExtractionSchema')}
              </p>
              <p className="mt-1.5 mb-0 text-body-sm text-ink-dim">
                {t('dataFusion.sensors.noExtractionSchemaBody', {
                  connector: connector?.name ?? '',
                })}
              </p>
            </div>
          )}
        </Panel>
      </div>

      {/* ── The signals ─────────────────────────────────────────────────
          Full width and below the device, because a signal is described in more
          fields than a device is and because the order is the order the questions
          arrive: which machine, then what it reads, then each reading. */}
      <section className="flex flex-col gap-3.5">
        <div className="flex flex-wrap items-end justify-between gap-x-5 gap-y-2">
          <div className="min-w-0">
            <Eyebrow>{t('dataFusion.sensors.sections.signals')}</Eyebrow>
            <p className="mt-1 mb-0 max-w-[72ch] text-body-sm text-ink-dim">
              <Num>{t('dataFusion.sensors.signalsIntro', { count: values.tags.length })}</Num>
            </p>
          </div>
          <ForgeButton onClick={onAddTag}>{t('dataFusion.sensors.addSignal')}</ForgeButton>
        </div>

        {errors.tags ? (
          <p role="alert" className="m-0 text-body-sm text-on-red">
            {errors.tags}
          </p>
        ) : null}

        {values.tags.map((tag, index) => (
          <TagPanel
            key={tag.key}
            index={index}
            tag={tag}
            extractionFields={extractionFields}
            waveformTypes={waveformTypes}
            valueUnits={valueUnits}
            errors={errors.tagErrors[index] ?? EMPTY_TAG_ERRORS}
            /* A device must keep at least one signal — `tags` is `min_length=1`,
               and a Pydantic length constraint returns a 422 that names no
               control. Removing the last one is prevented here rather than
               explained after the fact. */
            canRemove={values.tags.length > 1}
            onChange={(patch) => onTagChange(index, patch)}
            onExtractionChange={(key, value) => onTagExtractionChange(index, key, value)}
            onMetadataChange={(key, value) => onTagMetadataChange(index, key, value)}
            onRemove={() => onRemoveTag(index)}
            onOpenBuilder={(entry) => setBuilding({ field: entry, scope: index })}
          />
        ))}
      </section>

      {building ? (
        <JsonPathBuilder
          field={building.field}
          value={buildingValue}
          onCommit={(expression) => {
            if (building.scope === 'device') onExtractionChange(building.field.key, expression)
            else onTagExtractionChange(building.scope, building.field.key, expression)
            setBuilding(null)
          }}
          onClose={() => setBuilding(null)}
        />
      ) : null}
    </div>
  )
}

/** Nothing wrong with this signal. A shared frozen object, never mutated. */
const EMPTY_TAG_ERRORS = Object.freeze({
  extraction: Object.freeze({}),
  waveformMetadata: Object.freeze({}),
})

/* ══ ONE SIGNAL ════════════════════════════════════════════════════════ */

/**
 * One tag: what the reading is, what it is a reading of, and how to find it.
 *
 * ── Its name is optional, and the heading says which one this is anyway ───
 * `SensorTag.name` is `str | None`. A device with one signal rarely needs the
 * signal named separately from the device, and forcing a name there would mean
 * typing "Temperature" under a sensor already called "Oven 3 temperature". So the
 * panel is headed by its position, which is a fact whether or not a name is
 * given, and the name is offered as the thing that distinguishes signal two from
 * signal three.
 *
 * ── The extraction fields here are OVERRIDES ──────────────────────────────
 * Blank means "use the device's", which is what `buildSparseConfig` encodes by
 * omitting the key. Required is therefore not marked on these controls even when
 * the schema declares it: the requirement is on the MERGED view, and the device
 * may already have answered it. `SensorFormPage` validates the merge.
 */
function TagPanel({
  index,
  tag,
  extractionFields,
  waveformTypes,
  valueUnits,
  errors,
  canRemove,
  onChange,
  onExtractionChange,
  onMetadataChange,
  onRemove,
  onOpenBuilder,
}) {
  const { t } = useTranslation(['app', 'common'])

  /**
   * The selected waveform type, resolved from the catalogue on every render rather
   * than held in state beside its id.
   *
   * Two copies of one fact is one too many: the id is what the payload carries and
   * what the select binds to, so deriving the rest of the record from it means the
   * two can never disagree. Null when nothing is chosen yet, and also when a stored
   * id no longer resolves — see `waveformTypeById` for why that is a real state.
   */
  const waveformType = waveformTypeById(waveformTypes, tag.waveformTypeId)
  const metadataFields = waveformType?.metadataFields ?? []

  /* Namespaced per signal, and it has to be: the same schema key is rendered once
     per tag, and a duplicate DOM id binds every `<label for>` to the first match
     and points every ErrorSummary anchor at the wrong control. Matches the prefixes
     `SensorFormPage` builds its anchors from. */
  const idBase = `tag${index}`

  return (
    <Panel className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <Eyebrow>{t('dataFusion.sensors.signalN', { n: index + 1 })}</Eyebrow>
        {canRemove ? (
          <IconButton
            bare
            label={t('dataFusion.sensors.removeSignal', { n: index + 1 })}
            onClick={onRemove}
            className="size-8 hover:text-on-red"
          >
            <Icon name="trash" size="sm" />
          </IconButton>
        ) : null}
      </div>

      <div className="grid items-start gap-x-6 gap-y-4 xl:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Field
            id={`${idBase}-name`}
            label={t('dataFusion.sensors.fields.signalName')}
            hint={t('dataFusion.sensors.fields.signalNameHint')}
            maxLength={NAME_MAX_LENGTH}
            value={tag.name}
            error={errors.name}
            onChange={(event) => onChange({ name: event.target.value })}
          />

          <Field
            id={`${idBase}-description`}
            label={t('dataFusion.sensors.fields.signalDescription')}
            hint={t('dataFusion.sensors.fields.signalDescriptionHint')}
            maxLength={DESCRIPTION_MAX_LENGTH}
            value={tag.description}
            error={errors.description}
            onChange={(event) => onChange({ description: event.target.value })}
          />

          <ForgeSelect
            id={`${idBase}-value-type`}
            label={t('dataFusion.sensors.fields.valueType')}
            hint={t('dataFusion.sensors.fields.valueTypeHint')}
            required
            value={tag.valueType}
            onValueChange={(next) => onChange({ valueType: next })}
            options={VALUE_TYPES.map((type) => ({
              value: type,
              label: t(`dataFusion.sensors.valueType.${type}`),
            }))}
          />

          {/* Beside the value type, because the two answer one question together:
              how the reading is stored, and what it is a reading OF. Required with
              no default — there are seventeen units and no honest guess among
              them, so this is a deliberate choice rather than a pre-filled one. */}
          <UnitSelect
            id={`${idBase}-value-unit`}
            units={valueUnits}
            value={tag.valueUnitId}
            error={errors.valueUnitId}
            onChange={(next) => onChange({ valueUnitId: next })}
          />

          {/* ── Where this signal's value is, when it differs ─────────────
              Only rendered when the connector declares extraction fields at all;
              the device panel above says so when it does not, and repeating that
              on every signal would be the same sentence N times. */}
          {extractionFields.length ? (
            <>
              <p className="m-0 text-body-sm text-ink-dim">
                {t('dataFusion.sensors.overrideIntro')}
              </p>
              {extractionFields.map((field) => (
                <ConfigField
                  key={field.key}
                  idPrefix={`${idBase}-cfg`}
                  /* `required: false` regardless of what the schema says. The
                     requirement is on the merged view and the device may have
                     answered it already — marking it here would put an asterisk
                     on a control that is legitimately blank. */
                  field={{ ...field, required: false }}
                  value={tag.extraction[field.key]}
                  error={errors.extraction?.[field.key]}
                  onChange={(value) => onExtractionChange(field.key, value)}
                  onOpenBuilder={onOpenBuilder}
                />
              ))}
            </>
          ) : null}
        </div>

        {/* ── The signal's shape ────────────────────────────────────────
            What the value is expected to LOOK like — the shape, its engineering
            range, and the band outside which it is an alarm. That is what the
            anomaly detectors downstream read, and it is the one part of this panel
            describing the signal rather than the plumbing. */}
        <div className="flex flex-col gap-4">
          <ForgeSelect
            id={`${idBase}-waveform-type`}
            label={t('dataFusion.sensors.fields.waveformType')}
            hint={t('dataFusion.sensors.fields.waveformTypeHint')}
            required
            error={errors.waveformTypeId}
            placeholder={t('dataFusion.sensors.fields.chooseWaveform')}
            value={tag.waveformTypeId}
            onValueChange={(next) => onChange({ waveformTypeId: next })}
            options={waveformTypes.map((type) => ({ value: type.id, label: type.name }))}
          />

          {/* ── The fields arrive with the choice, and outlive it ─────────
              Generated from the SELECTED type's `metadata_schema`, so switching
              from Constant to Sinusoidal grows an "Update Interval" field and
              switching back removes it.

              What it does NOT do is clear the four shared values. `range.min` means
              the same thing under every shape, and a form that discarded a typed
              engineering range because someone corrected the waveform would be
              punishing them for the correction. The values are held flat and only
              the selected type's keys are read on submit, so an orphaned
              `update_interval` sits in state and is never sent. */}
          {tag.waveformTypeId ? (
            metadataFields.length ? (
              metadataFields.map((field) => (
                <ConfigField
                  key={field.key}
                  idPrefix={`${idBase}-wf`}
                  field={field}
                  value={tag.waveformMetadata[field.key]}
                  error={errors.waveformMetadata?.[field.key]}
                  onChange={(value) => onMetadataChange(field.key, value)}
                />
              ))
            ) : (
              /* A selected id the catalogue does not list, or one whose schema is
                 empty. Reachable the same way `noExtractionSchema` is: these tables
                 are seeded, and a tag holds a foreign key into them. Saying so beats
                 rendering an empty panel that looks finished. */
              <p className="m-0 text-body-sm text-on-amber">
                {t('dataFusion.sensors.noWaveformSchema')}
              </p>
            )
          ) : (
            <p className="m-0 text-body-sm text-ink-dim">{t('dataFusion.sensors.waveformFirst')}</p>
          )}
        </div>
      </div>
    </Panel>
  )
}

/**
 * Where the sensor attaches, stated rather than offered.
 *
 * A `StaticField` and not a disabled cascade: three greyed-out selects look like
 * controls, take a hover, and teach someone to keep trying them. This states the
 * placement as the fact it now is.
 *
 * An id that no longer resolves is shown raw with a warning — deletion is soft
 * everywhere in this API and nothing cascades, so a sensor outlives the machine it
 * was attached to.
 */
function LockedParent({ nodes, values }) {
  const { t } = useTranslation(['app', 'common'])

  const parent = values.parent ?? parentOf(values)
  const attached = describeParent(nodes, parent)

  return (
    <StaticField
      label={t('dataFusion.sensors.fields.parent')}
      hint={t('dataFusion.sensors.fields.parentLocked')}
      icon={attached ? PARENT_ICON[attached.tier] : 'bolt'}
    >
      {attached ? (
        <span className="min-w-0 truncate">
          {attached.name}
          {attached.trail.length ? (
            <span className="text-ink-dim">{` · ${attached.trail.join(' · ')}`}</span>
          ) : null}
        </span>
      ) : (
        <span className="min-w-0 truncate text-on-amber">
          {t('dataFusion.sensors.parentUnresolved')}
        </span>
      )}
    </StaticField>
  )
}

/**
 * `values.parent` → what `PlaceField` shows.
 *
 * A parent chosen in the picker carries its own name. One that did not come
 * from the picker — seeded from the connection's site on a new device — has
 * only `{tier, id}`, so its name is read off the hierarchy the page already
 * holds, and falls back to the id rather than to nothing.
 */
function placeValue(nodes, parent) {
  if (!parent?.id) return null
  if (parent.name) return parent
  const node = nodes.find((entry) => entry.id === parent.id)
  return { ...parent, name: node?.name ?? parent.id }
}

/** "No asset class". Radix rejects an item whose value is the empty string. */
const NO_CLASS = '__no_class__'

/**
 * The engineering unit, named and symbolised in one option.
 *
 * Both halves are shown because neither is sufficient. "psi" alone is unreadable
 * to anyone who has not met it; "Pounds per Square Inch" alone does not match what
 * is printed beside a reading. So the label carries the name and the symbol in
 * parentheses — which is also what makes Radix's typeahead find `°C` by typing
 * "cel".
 *
 * `count` has an empty symbol, legitimately, so the parenthesis is conditional
 * rather than always appended. `ForgeSelect` renders a plain string per option and
 * cannot take markup, so the symbol cannot be a `<Mono>` here; the name-first
 * ordering is what keeps the list alphabetically scannable instead.
 */
function UnitSelect({ id, units, value, error, onChange }) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <ForgeSelect
      id={id}
      label={t('dataFusion.sensors.fields.valueUnit')}
      hint={t('dataFusion.sensors.fields.valueUnitHint')}
      required
      error={error}
      placeholder={t('dataFusion.sensors.fields.chooseUnit')}
      value={value}
      onValueChange={onChange}
      options={units.map((unit) => ({
        value: unit.id,
        label: unit.symbol ? `${unit.name} (${unit.symbol})` : unit.name,
      }))}
    />
  )
}

/**
 * A compact restatement of a signal's extraction path, for the table and the
 * delete confirmation — wherever a sensor has to be identified by what it reads
 * rather than by its name.
 *
 * Reads whichever key the connector type calls its value path rather than assuming
 * `value_path`: the schema is connector-specific and this is display only, so it
 * takes the first `jsonpath` field it finds and states nothing when there is none.
 *
 * The tag's own config first and the device's behind it, which is the order the
 * server merges them in — a path shown here has to be the one that signal actually
 * reads, not the default it overrode.
 */
export function primaryPathOf(connector, deviceConfig, tagConfig) {
  const field = (connector?.extractionFields ?? []).find((entry) => entry.type === 'jsonpath')
  if (!field) return null
  const value = tagConfig?.[field.key] ?? deviceConfig?.[field.key]
  return typeof value === 'string' && value ? value : null
}

/** The same, rendered. Machine string: monospace, never translated. */
export function PathCell({ path }) {
  if (!path) return <span className="text-ink-mute">—</span>
  return <Mono className="block max-w-[22rem] truncate text-ink-dim">{path}</Mono>
}
